import { memo, useState } from "react";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import WalletIcon from '@mui/icons-material/Wallet';
import { useTypedSelector } from "hooks";
import { FormAdapter } from "hooks/useForm";
import { delay } from "util/support";
import { partialRefund } from "features/transactions/api";
import { selectIsTransactionDetailsLoading } from "features/transactions/selectors";
import useTransactionActions from "features/transactions/useActions";
import { useModal } from "ui/atoms/Modal";
import { BasicDialog } from "ui/molecules/Dialog";
import When from "ui/support/When";
import RefundAmountForm, { type RefundAmount } from "ui/forms/Transaction/RefundAmount";
import SubmitRefund from "./SubmitRefund";
import { useRefund } from "../support";
import useTransactionAction from "../useTransactionAction";

const PartialRefund = () => {
    const {
        transaction,
        request
    } = useTransactionAction();

    const [isLoading, setLoading] = useState(false);

    const { getTransactionDetails } = useTransactionActions();
    const isTransactionDetailsLoading = useTypedSelector(selectIsTransactionDetailsLoading) || isLoading;

    const {
        coreId,
        MOR,
        refundableAmount: amount
    } = transaction;

    const { isPartialRefundAvailable } = useRefund(transaction);

    const {
        open,
        onClose,
        onOpen
    } = useModal();

    const handleRefund = async ({ amount }: Partial<RefundAmount>) => {
        setLoading(true);

        const response = await request(() => partialRefund({
            coreId,
            MOR,
            amount: Number(amount)
        }));

        await delay(2000);

        const [refresh] = getTransactionDetails(coreId);

        await refresh;

        setLoading(false);
        return FormAdapter.fromResponse(response);
    };

    return (
        <When
            if={isPartialRefundAvailable}
            then={() => (
                <>
                    <LoadingButton
                        data-test-id="transactions.transactionActions.partialRefund"
                        onClick={onOpen}
                        variant='outlined'
                        size='small'
                        startIcon={<WalletIcon />}
                        loading={isTransactionDetailsLoading}
                    >
                        Refund
                    </LoadingButton>
                    <BasicDialog
                        isOpen={open}
                        isCanClose={false}
                        onClose={onClose}
                        DialogProps={{
                            sx: {
                                '& .MuiDialog-container .MuiPaper-root': {
                                    width: '100%',
                                    maxWidth: 445
                                }
                            }
                        }}
                    >
                        <RefundAmountForm
                            SubmitActionComponent={form => (
                                <SubmitRefund
                                    {...form}
                                    isSubmitting={isTransactionDetailsLoading}
                                    onCancel={onClose}
                                />
                            )}
                            onSaveOrCreate={handleRefund}
                            initialValues={{
                                amount
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: 'inherit',
                                    fontSize: 22,
                                    fontWeight: 600
                                }}
                                gutterBottom
                            >
                                Refund
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: 'inherit',
                                    fontSize: 14,
                                    fontWeight: 500,
                                    mb: 2
                                }}
                                gutterBottom
                            >
                                Do you want to proceed with total or partial refunding for this transaction?
                            </Typography>
                        </RefundAmountForm>
                    </BasicDialog>
                </>
            )}
        />
    );
};

export default memo(PartialRefund);
